/** A leaf may temporarily span several native nodes after splitText or autocorrect. */
export interface LeafDOM { id: string; nodes: ChildNode[]; parent: HTMLElement; next?: Node | null }
const identities = new WeakMap<Node, LeafDOM>()
const roots = new WeakMap<HTMLElement, Map<string, LeafDOM>>()

export function mappedLeaf(node: Node | null): LeafDOM | undefined {
  for (let at = node; at; at = at.parentNode) {
    const leaf = identities.get(at)
    if (leaf) return leaf
  }
  return undefined
}

export function findLeaf(root: HTMLElement, id: string): LeafDOM | undefined { return roots.get(root)?.get(id) }

export function bindLeaf(root: HTMLElement, id: string, node: ChildNode, parent: HTMLElement): LeafDOM {
  let leaves = roots.get(root)
  if (!leaves) { leaves = new Map(); roots.set(root, leaves) }
  const old = leaves.get(id)
  if (old?.nodes.length === 1 && old.nodes[0] === node) { old.parent = parent; return old }
  if (old) for (const previous of old.nodes) identities.delete(previous)
  const leaf = { id, nodes: [node], parent }
  identities.set(node, leaf)
  leaves.set(id, leaf)
  return leaf
}

export function forgetLeaf(root: HTMLElement, id: string, parent: HTMLElement) {
  const leaves = roots.get(root)
  const leaf = leaves?.get(id)
  if (leaf?.parent !== parent) return
  for (const node of leaf.nodes) identities.delete(node)
  leaves?.delete(id)
}

export function leafText(leaf: LeafDOM): string { return leaf.nodes.map(node => node.textContent ?? '').join('') }

/** Retain identity when the platform replaces or splits a Text instead of changing its data. */
export function adoptLeafMutations(records: MutationRecord[]) {
  const groups = new Map<Node, MutationRecord[]>()
  for (const record of records) if (record.type === 'childList') {
    const group = groups.get(record.target) ?? []
    group.push(record)
    groups.set(record.target, group)
  }
  for (const [parent, group] of groups) {
    const removed = group.flatMap(record => Array.from(record.removedNodes, node => node as ChildNode))
      .filter(node => !parent.contains(node))
    const owners = [...new Set(removed.map(node => identities.get(node)).filter(owner => owner !== undefined))]
    const replacements = new Map<ChildNode, LeafDOM>()
    for (const [index, record] of group.entries()) {
      const owner = Array.from(record.removedNodes).map(node => identities.get(node)).find(value => value !== undefined)
      if (!owner) continue
      const additions = record.addedNodes.length > 0 ? record.addedNodes : group[index - 1]?.addedNodes ?? []
      for (const node of Array.from(additions)) replacements.set(node as ChildNode, owner)
    }
    for (const owner of owners) {
      owner.next = group.find(record => Array.from(record.removedNodes).some(node => owner.nodes.includes(node as ChildNode)))?.nextSibling
      owner.nodes = owner.nodes.filter(node => !removed.includes(node))
    }
    for (const node of removed) identities.delete(node)
    const added = new Set(group.flatMap(record => Array.from(record.addedNodes, node => node as ChildNode)))
    for (const node of Array.from(parent.childNodes)) {
      if (!added.has(node) || identities.has(node)) continue
      // Renderer commits introduce semantic blocks; only native text replacements belong to a leaf.
      if (node instanceof Element && (node.matches('button, input, textarea, select, label, [role="button"], [aria-hidden="true"], [contenteditable="false"]') || 'blockId' in (node as HTMLElement).dataset || node.querySelector('[data-block-id]'))) continue
      // Read final sibling order: replacement may arrive as separate add/remove records,
      // and adapters need not supply MutationRecord's optional sibling references.
      const previous = node.previousSibling && identities.get(node.previousSibling)
      const next = node.nextSibling && identities.get(node.nextSibling)
      const owner = replacements.get(node) ?? previous ?? next ?? owners[0]
      if (!owner || owner.parent !== parent) continue
      identities.set(node, owner)
      owner.nodes.push(node)
      owner.nodes.sort((a, b) => a === b ? 0 : a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1)
    }
  }
}

/** Direct leaves plus unchanged React-island leaves, in no semantically significant order. */
export function renderedLeaves(root: HTMLElement): LeafDOM[] {
  const direct = Array.from(roots.get(root)?.values() ?? []).filter(leaf => root.contains(leaf.parent))
  const islands = Array.from(root.querySelectorAll<HTMLElement>('[data-leaf="true"]'))
    .filter(node => !identities.has(node)).map(node => ({ id: node.dataset.blockId ?? '', nodes: [node], parent: node }))
  return [...direct, ...islands]
}

export function leafPoint(leaf: LeafDOM, offset: number): { node: HTMLElement | Text; offset: number } {
  let remaining = offset
  let last: { node: HTMLElement | Text; offset: number } = { node: leaf.parent, offset: 0 }
  for (const root of leaf.nodes) {
    const nodes: Node[] = root instanceof Text ? [root] : []
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT)
    for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node)
    for (const node of nodes) {
      if (node instanceof Text) {
        if (remaining <= node.length) return { node, offset: remaining }
        remaining -= node.length
        last = { node, offset: node.length }
      } else if (node instanceof HTMLBRElement && node.parentElement) {
        const index = Array.from(node.parentElement.childNodes).indexOf(node)
        if (remaining === 0) return { node: node.parentElement, offset: index }
        remaining--
        last = { node: node.parentElement, offset: index + 1 }
      }
    }
  }
  return last
}

export function leafOffset(leaf: LeafDOM, node: Node, offset: number): number {
  let before = 0
  for (const part of leaf.nodes) {
    if (part === node || part.contains(node)) {
      const range = document.createRange()
      range.setStart(part, 0)
      range.setEnd(node, offset)
      return before + range.toString().length + range.cloneContents().querySelectorAll('br').length
    }
    before += (part.textContent ?? '').length
  }
  return before
}

/** Restore the rendered text before the core transaction patches its canonical version. */
export function restoreLeaf(leaf: LeafDOM, text: string) {
  let first = leaf.nodes[0]
  if (!first) {
    first = document.createTextNode(text)
    leaf.parent.insertBefore(first, leaf.next?.parentNode === leaf.parent ? leaf.next : null)
    leaf.nodes = [first]
    identities.set(first, leaf)
  }
  if (first instanceof Text) {
    first.data = text
    for (const node of leaf.nodes.slice(1)) { identities.delete(node); node.remove() }
    leaf.nodes = [first]
  } else if (first) {
    const node = document.createTreeWalker(first, NodeFilter.SHOW_TEXT).nextNode()
    if (node instanceof Text) node.data = text
  }
}
